import React, { FC, useContext } from 'react';
import { Forward, Gross, Transfer } from 'ts/transfer/interfaces/TransferResultsResponse';
import { useTranslation } from 'react-i18next';
import TransferResultMap from './ResultMap';
import TransferResultInfoSm from './ResultInfoSm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useSearchParams } from 'react-router-dom';
import { getCurrency, formatPriceCurrency } from 'utils/utils';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import SalesPrice from 'components/shared/SalesPrice';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { MainContext } from 'contexts/MainContext';

interface TransferResultInfoProps {
  mapTransfer: Transfer;
  choosenArrival?: Forward;
  choosenReturn?: Forward;
  oneWay: boolean;
  currency: keyof Gross;
  couponAmount?: number;
  setCouponAmount?: (value: number) => void;
  recountTotalPrice?: () => void;
  totalPrice?: number;
  goToDetails?: () => void;
}

const TransferResultInfo: FC<TransferResultInfoProps> = ({
  choosenArrival,
  choosenReturn,
  oneWay,
  ...props
}) => {
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const [t] = useTranslation();
  const [urlParams] = useSearchParams();
  const fromTo = {
    from: urlParams.get('pickup.desc'),
    to: urlParams.get('dropoff.desc'),
  };

  const { setProposal, proposal } = useContext(MainContext);
  const searchToken = sessionStorage.getItem('transferSearchToken') || '';

  const getPrice = () => {
    if (choosenArrival && choosenReturn) {
      return (
        (choosenArrival.transfer.transferVehicles[0].itemPrice.gross[props.currency] as number) +
        (choosenReturn.transfer.transferVehicles[0].itemPrice.gross[props.currency] as number)
      );
    } else if (choosenArrival && oneWay) {
      return choosenArrival.transfer.transferVehicles[0].itemPrice.gross[props.currency] as number;
    }
    return 0;
  };

  const onClickProposal = () => {
    if (!proposal || !setProposal) return;

    const existingProposalIndex = proposal.transfers.findIndex(
      (proposal) =>
        proposal.forwardItem?.priceKey === choosenArrival?.priceKey &&
        proposal.returnItem?.priceKey === choosenReturn?.priceKey
    );

    if (existingProposalIndex !== -1) {
      const updatedTransfers = proposal.transfers.filter(
        (_, index) => index !== existingProposalIndex
      );
      setProposal({ ...proposal, transfers: updatedTransfers });
    } else {
      const newProposal = {
        searchToken,
        forwardItem: choosenArrival,
        returnItem: choosenReturn,
        currency: props.currency,
        price: getPrice(),
        displayPrice: true,
      };

      setProposal({ ...proposal, transfers: [...proposal.transfers, newProposal] });
    }
  };

  return (
    <div className="transfer-results-info">
      <TransferResultMap mapTransfer={props.mapTransfer} />

      {(!!choosenArrival || !!choosenReturn) && (
        <div className="default-shadow transfer-sum transfer-sum-data position-relative">
          {!!choosenArrival && (
            <ProposalHeart
              active={
                !!proposal?.transfers.some(
                  (prop) =>
                    prop.forwardItem?.priceKey === choosenArrival?.priceKey &&
                    prop.returnItem?.priceKey === choosenReturn?.priceKey
                )
              }
              onClickPropsal={onClickProposal}
            />
          )}

          <div className="title-underline text-lg title-header-grey">
            {t('passenger.booking_summary')}
          </div>
          {!!choosenArrival && (
            <TransferResultInfoSm
              data={choosenArrival}
              title="transfer.arrival_details"
              currency={props.currency}
            />
          )}
          {!!choosenReturn && (
            <TransferResultInfoSm
              data={choosenReturn}
              title="transfer.return_details"
              currency={props.currency}
            />
          )}

          {!!choosenArrival && !choosenReturn && !oneWay && (
            <div
              className="color-danger text-center mt-20 mb-10 text-bold"
              dangerouslySetInnerHTML={{ __html: t('transfer.choose_return', fromTo) }}
            ></div>
          )}

          {!choosenArrival && !!choosenReturn && !oneWay && (
            <div
              className="color-danger text-center mt-20 mb-10 text-bold"
              dangerouslySetInnerHTML={{ __html: t('transfer.choose_arrival', fromTo) }}
            ></div>
          )}
          {((!!choosenArrival && !!choosenReturn) || (!!choosenArrival && oneWay)) && (
            <div className="w100p text-center mt-20 title-footer-grey">
              <div className="text-bold mb-10 text-lg">
                {t('total')}: {formatPriceCurrency(getPrice(), getCurrency(props.currency))}
              </div>
              {!!props.goToDetails && (
                <FontAwesomeIcon
                  className="cur-poi"
                  icon={faCircleArrowRight}
                  color={colorContrast}
                  size="xl"
                  onClick={() => props.goToDetails?.()}
                />
              )}
            </div>
          )}
        </div>
      )}

      {props.couponAmount !== undefined && (
        <SalesPrice
          currency={props.currency}
          price={props.totalPrice!}
          saleValue={props.couponAmount + ''}
          setSaleValue={(value) => props.setCouponAmount?.(+value)}
          onClickApply={() => props.recountTotalPrice?.()}
        />
      )}
    </div>
  );
};

export default withErrorBoundary(TransferResultInfo, 'TransferResultInfo');
